import { API_ROUTES } from "@constants/api-routes";
import { useToast } from "@context/ToastContext";
import { apiService } from "@services/axios";
import { useCallback } from "react";
import {
  CampaignVisibleFieldsConfig,
  LeadContact,
} from "../types/lead-contacts.types";
import { buildCopyLines, copyLinesToText } from "../utils/buildCopyLines";
import { getExtraFields } from "../utils/contactOthers";

export type GetConfigForCampaign = (
  campaignId: number,
) => CampaignVisibleFieldsConfig | undefined;

export const useLeadContactsActions = (getConfig?: GetConfigForCampaign) => {
  const { showError, showToast } = useToast();

  const copyLeadId = useCallback(
    (leadId: number) => {
      if (!leadId || isNaN(leadId)) {
        showError("ID do lead inválido");
        return;
      }

      navigator.clipboard
        .writeText(leadId.toString())
        .then(() => {
          showToast({
            type: "success",
            message: `ID do lead #${leadId} copiado!`,
          });
        })
        .catch(() => {
          showError("Erro ao copiar ID do lead");
        });
    },
    [showError, showToast],
  );

  const copyContactInfo = useCallback(
    async (
      contact: LeadContact,
      updateCopiedLeads: (leadIds: number[]) => void,
    ) => {
      const extras = getExtraFields(contact.others);
      const config = getConfig?.(contact.campaign_id);
      const lines = buildCopyLines(contact, extras, config);
      const info = copyLinesToText(lines);

      try {
        await navigator.clipboard.writeText(info);

        const leadIdToCopy = contact.is_quality_lead
          ? contact.id
          : contact.lead_id;
        updateCopiedLeads([leadIdToCopy]);

        try {
          await apiService.post(API_ROUTES.LEAD_COPY.RECORD, {
            leadId: contact.lead_id,
            copyType: "individual",
          });
        } catch (error) {
          console.warn("Erro ao registrar cópia no banco:", error);
        }

        showToast({
          type: "success",
          message: "Informações copiadas para a área de transferência!",
        });
      } catch {
        showError("Erro ao copiar informações");
      }
    },
    [showError, showToast, getConfig],
  );

  const copyPhoneNumber = useCallback(
    (phone?: string) => {
      const toCopy = (phone || "").trim();
      if (!toCopy) {
        showError("Telefone não disponível");
        return;
      }
      navigator.clipboard
        .writeText(toCopy)
        .then(() => {
          showToast({ type: "success", message: "Telefone copiado!" });
        })
        .catch(() => showError("Erro ao copiar telefone"));
    },
    [showError, showToast],
  );

  const copyBatchContacts = useCallback(
    async (
      selectedContacts: LeadContact[],
      updateCopiedLeads: (leadIds: number[]) => void,
      clearSelection: () => void,
    ) => {
      const copyText = selectedContacts
        .map((contact) => {
          const extras = getExtraFields(contact.others);
          const config = getConfig?.(contact.campaign_id);
          const lines = buildCopyLines(contact, extras, config);
          return copyLinesToText(lines);
        })
        .join("\n\n" + "=".repeat(50) + "\n\n");

      try {
        await navigator.clipboard.writeText(copyText);

        const leadIds = selectedContacts.map((contact) =>
          contact.is_quality_lead ? contact.id : contact.lead_id,
        );
        updateCopiedLeads(leadIds);

        try {
          await apiService.post(API_ROUTES.LEAD_COPY.RECORD_BATCH, {
            leadIds,
            copyType: "batch",
          });
        } catch (error) {
          console.warn("Erro ao registrar cópia no banco:", error);
        }

        showToast({
          type: "success",
          message: `✅ ${selectedContacts.length} leads copiados para a área de transferência!`,
        });
        clearSelection();
      } catch {
        showError("Erro ao copiar informações");
      }
    },
    [showError, showToast, getConfig],
  );

  return {
    copyLeadId,
    copyContactInfo,
    copyPhoneNumber,
    copyBatchContacts,
  };
};
